import { Filters, IdType, Row, SortingRule } from 'react-table';
import CIcon from '@coreui/icons-react';
import { CPagination } from '@coreui/react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import MCard from 'views/MCard/MCard';
import MDateNavigator from 'views/MDateNavigator/MDateNavigator';
import MCheckBox from 'views/MCheckBox/MCheckBox';
import { MDateNavigatorDirection } from 'views/MDateNavigator/MDateNavigator.model';
import MSearchInput from 'views/MSearchInput/MSearchInput';
import MSimpleSelect from 'views/MSimpleSelect/MSimpleSelect';
import MButton from '../../Buttons/MButton/MButton';
import MDropdownContainer from 'views/MDropdown/MDropdownContainer/MDropdownContainer';
import MTable from '../MTable';

import { MColumnOptions, RowData, TableRef } from '../Table.model';
import classes from './MServerTable.module.scss';
import { IDropdownAction } from 'views/MDropdown/MDropdown.model';

interface Props<T extends RowData> {
  className?: string;
  rowClassName?: string;
  tableRef?: React.RefObject<TableRef<T>>;
  limit: number;
  data: T[];
  loading: boolean;
  pages: number;
  page: number;
  sort: SortingRule<T>[];
  allResults: boolean;
  count: number;
  disableFilters?: boolean;
  showDateNavigator?: boolean;
  disablePagination?: boolean;
  showOnlyWithGeneralFilter?: boolean;
  headerButtons?: JSX.Element | JSX.Element[] | null;
  columns: string[];
  columnsOptions?: MColumnOptions<T>;
  hiddenColumns: string[];
  hideFooter?: boolean;
  openExpandLabel?: string;
  closeExpandLabel?: string;
  filters: Filters<T>;
  hideColumnSelect?: boolean;
  selectedRow?: Partial<T> | Partial<T>[];
  hideSelection?: boolean;
  filtersJSX?: { id: IdType<T>; jsxFunc: () => React.ReactNode }[];
  footerDataView?: JSX.Element | JSX.Element[];
  generalFilterValue: string;
  generalFilterPlaceholder?: string;
  hideGeneralFilter: boolean;
  columnItems: IDropdownAction<boolean>[];
  deleteButton?: boolean;
  disableDeleteBtn?: boolean;
  onDeleteBtnClick?: () => void;
  onChangeColumnItem: (action: IDropdownAction<boolean>, index: number) => void;
  onLimitChange: (event: React.ChangeEvent<HTMLSelectElement>) => void;
  onDateNavigatorClick: (direction: MDateNavigatorDirection) => void;
  onAllResultsChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onClearButtonClick: () => void;
  onSortChange: (sortValue: SortingRule<T>[]) => void;
  onPageChange: (page: number) => void;
  onSelectChange: (selected: string[]) => void;
  onFiltersChange?: (filters: Filters<T>) => void;
  getRowId?: (row: T) => string;
  renderExpand?: (row: Row<T>) => JSX.Element;
  onFiltersRender?: (filters: { id: IdType<T>; jsxFunc: () => React.ReactNode }[]) => void;
  onGeneralFilterChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onRowClick?: (row: Row<T>) => void;
}

const MServerTableView = <T extends RowData>(props: React.PropsWithChildren<Props<T>>): JSX.Element => {
  const { t } = useTranslation();

  return (
    <MCard className={[classes['container'], props.className].join(' ')}>
      <div className={classes['header']}>
        <div className={`${classes['headerSection']} ${classes['headerSection--left']}`}>
          {props.headerButtons}
          {!props.hideGeneralFilter ? (
            <MSearchInput
              value={props.generalFilterValue}
              size={props.showOnlyWithGeneralFilter ? 'l' : 's'}
              placeholder={props.generalFilterPlaceholder}
              onChange={props.onGeneralFilterChange}
            ></MSearchInput>
          ) : null}
          {props.filtersJSX?.map((filter: { id: IdType<T>; jsxFunc: () => React.ReactNode }) => {
            return filter.jsxFunc();
          })}
        </div>
        <div className={classes['headerSection']}>
          {props.showDateNavigator ? (
            <MDateNavigator className={classes['dateSection']} onClick={props.onDateNavigatorClick}></MDateNavigator>
          ) : null}
          {props.deleteButton ? (
            <MButton type="button" color="transparent" onClick={props.onDeleteBtnClick} disabled={props.disableDeleteBtn}>
              <CIcon name="cil-trash" />
            </MButton>
          ) : null}
          {!props.hideColumnSelect && (
            <MDropdownContainer actions={props.columnItems} onSelect={props.onChangeColumnItem} type="checkbox">
              {(clickHandler: () => void) => {
                return (
                  <MButton type="button" color="transparent" onClick={clickHandler}>
                    <CIcon name="cil-eye" className={classes['settingsIcon']} />
                  </MButton>
                );
              }}
            </MDropdownContainer>
          )}
          {!props.disableFilters && (
            <MButton className={classes['action']} color="transparent" onClick={props.onClearButtonClick}>
              {t('views.button.text.clearFilter')}
            </MButton>
          )}
          {!props.disablePagination && (
            <div className={classes['numberOfRowsDisplay']}>
              <MSimpleSelect
                options={[
                  { value: 10, label: '10' },
                  { value: 20, label: '20' },
                  { value: 50, label: '50' },
                  { value: 100, label: '100' },
                ]}
                value={props.limit}
                onChange={props.onLimitChange}
              ></MSimpleSelect>
            </div>
          )}
        </div>
      </div>
      {!props.showOnlyWithGeneralFilter || props.generalFilterValue ? (
        <MTable
          ref={props.tableRef}
          className={classes['table']}
          data={props.data}
          columns={props.columns}
          columnsOptions={props.columnsOptions}
          hiddenColumns={props.hiddenColumns}
          disableFilters={props.disableFilters}
          onSortChange={props.onSortChange}
          onFiltersChange={props.onFiltersChange}
          onSelect={props.onSelectChange}
          page={props.page - 1}
          pageCount={props.pages}
          manualFilters
          loading={props.loading}
          manualSortBy
          getRowId={props.getRowId}
          renderExpand={props.renderExpand}
          openExpandLabel={props.openExpandLabel}
          closeExpandLabel={props.closeExpandLabel}
          filters={props.filters}
          selectedRow={props.selectedRow}
          hideSelection={props.hideSelection}
          rowClassName={props.rowClassName}
          onFiltersRender={props.onFiltersRender}
          onRowClick={props.onRowClick}
          sort={props.sort}
        ></MTable>
      ) : null}
      {!props.hideFooter && (
        <div className={classes['footer']}>
          <div className={classes['footerTotals']}>
            {!props.hideSelection && !props.disablePagination ? (
              <React.Fragment>
                <MCheckBox id="allResults" checked={props.allResults} onChange={props.onAllResultsChange}></MCheckBox>
                <label htmlFor="allResults">{t('common.table.footer.allResult').toString()}</label>
              </React.Fragment>
            ) : null}
            <span className={classes['total']}>
              {t('common.table.total').toString()}: {props.count}
            </span>
            {props.footerDataView}
   
          </div>

          {props.pages > 1 && !props.disablePagination && !props.loading ? (
            <CPagination
              pages={props.pages}
              limit={7}
              activePage={props.page}
              onActivePageChange={props.onPageChange}
              dots
              arrows
              doubleArrows
              addListClass={classes['innerPagination']}
            ></CPagination>
          ) : null}
        </div>
      )}
    </MCard>
  );
};

MServerTableView.displayName = 'MServerTableView';

export default MServerTableView;
